import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import Phone from "@insite/mobius/Icons/Phone";
import Link from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    isGuest: getCurrentUserIsGuest(state),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface DeliveryStatusAndDetailsStyles {
    wrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyProps;
    contactLinksWrapper?: InjectableCss;
    contactLink?: InjectableCss;
    phoneIcon?: ButtonPresentationProps;
    linkText?: TypographyProps;
}

export const deliveryStatusAndDetailsStyles: DeliveryStatusAndDetailsStyles = {
    wrapper: {
        css: css`
            margin-bottom: 20px;
        `,
    },
    titleWrapper: {
        css: css`
            display: flex;
            gap: 30px;
            align-items: center;
            margin-bottom: 10px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                        align-items: flex-start;
                    `,
                    css`
                        flex-direction: column;
                        align-items: flex-start;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    title: {
        variant: "h4",
        css: css`
            margin: 0;
        `,
    },
    contactLinksWrapper: {
        css: css`
            display: flex;
            gap: 20px;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                        align-items: flex-start;
                    `,
                    css`
                        flex-direction: column;
                        align-items: flex-start;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    contactLink: {
        css: css`
            display: flex;
            align-items: center;
            gap: 5px;
            text-decoration: none;
            color: inherit;
            &:hover {
                text-decoration: none;
            }
            > span {
                display: flex;
                align-items: center;
            }
        `,
    },
    phoneIcon: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            > svg {
                stroke-width: 1px;
            }
            margin-right: 5px;
        `,
    },
    linkText: {
        size: 14,
        color: "text.link",
        css: css`
            line-height: 20px;
            &:hover {
                text-decoration: none;
            }
        `,
    },
};

const styles = deliveryStatusAndDetailsStyles;

const DeliveryStatusAndDetails = ({ fields, isGuest }: Props) => {
    const { value: order } = useContext(OrderStateContext);

    if (!order) {
        return null;
    }

    const accountManagerPhone = order.properties?.jobSitePhone;
    const warehouseDetails = order?.properties?.warehouseDetails ? JSON.parse(order.properties.warehouseDetails) : null;
    const localSupportPhone = warehouseDetails?.phone;

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.titleWrapper}>
                <Typography {...styles.title}>{fields.title || "Delivery Status and Details"}</Typography>
                <StyledWrapper {...styles.contactLinksWrapper}>
                    {!isGuest && accountManagerPhone && (
                        <Link href={`tel:${accountManagerPhone}`} {...styles.contactLink}>
                            <Icon src={Phone} size={16} {...styles.phoneIcon} />
                            <Typography {...styles.linkText}>{translate("Contact Account Manager")}</Typography>
                        </Link>
                    )}
                    {localSupportPhone && (
                        <Link href={`tel:${localSupportPhone}`} {...styles.contactLink}>
                            <Icon src={Phone} size={16} {...styles.phoneIcon} />
                            <Typography {...styles.linkText}>{translate("Contact Local Support")}</Typography>
                        </Link>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(DeliveryStatusAndDetails),
    definition: {
        allowedContexts: ["OrderDetailsPage"],
        displayName: "Delivery Status and Details",
        group: "Order Details",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Delivery Status and Details",
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
